import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { SelectionPanel } from '@/components/SelectionPanel';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMeridianUser } from '@/contexts/FeatureFlagContext';
import { SelectionProvider } from '@/contexts/SelectionContext';

import { ExecutionWizard } from './ExecutionWizard';

export function EvalLayout({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showWizard, setShowWizard] = useState(false);

  // Check if user has admin access (uses domain + allowlist from FeatureFlagContext)
  const isAdmin = useIsMeridianUser();

  const navItems = [
    { href: '/eval/prompts', label: '📝 Prompts', icon: '📝' },
    { href: '/eval/configs', label: '⚙️  Model Configs', icon: '⚙️' },
    { href: '/eval/results?groupBy=run', label: '📊 Eval Results', icon: '📊' },
    { href: '/eval/unified', label: '🗳️ Unified Prompts', icon: '🗳️' },
    { href: '/eval/llm-judge', label: '🤖 LLM Judge', icon: '🤖' },
    { href: '/eval/user-stats', label: '👥 User Stats', icon: '👥' },
  ];

  // Show access denied if not admin
  if (!isAdmin) {
    return (
      <div
        className="flex items-center justify-center min-h-screen p-8"
        style={{ background: '#F5F0EB' }}
      >
        <div
          className="max-w-lg bg-white rounded-xl p-12 text-center"
          style={{ border: '1px solid #E8E0D8' }}
        >
          <h1
            className="font-serif text-3xl font-semibold mb-4"
            style={{ color: '#1A1A1A' }}
          >
            Access Denied
          </h1>
          <p className="text-lg mb-2" style={{ color: '#6B6B6B' }}>
            This admin panel is only accessible to Meridian administrators.
          </p>
          <p className="text-sm" style={{ color: '#A0A0A0' }}>
            {user
              ? `Signed in as: ${user.email}`
              : 'Please sign in with a Meridian account.'}
          </p>
          <Link to="/" className="inline-block mt-8">
            <Button variant="primary">← Back to Arena</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <SelectionProvider>
      <div className="flex min-h-screen" style={{ background: '#F5F0EB' }}>
        {/* Sidebar */}
        <aside
          className="w-60 bg-white fixed h-screen overflow-y-auto py-8"
          style={{ borderRight: '1px solid #E8E0D8' }}
        >
          <div className="px-6 mb-8">
            <Link to="/" className="no-underline" style={{ color: '#1A1A1A' }}>
              <h1 className="text-xl font-semibold m-0">← Arena</h1>
            </Link>
            <h2
              className="font-serif text-2xl font-semibold mt-2 mb-0"
              style={{ color: '#8B2332' }}
            >
              Admin Panel
            </h2>
          </div>

          <nav>
            {navItems.map(item => {
              const isActive = location.pathname.startsWith(
                item.href.split('?')[0]
              );
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className="block px-6 py-3 no-underline transition-all duration-150"
                  style={{
                    fontSize: '0.9375rem',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? '#8B2332' : '#1A1A1A',
                    background: isActive
                      ? 'rgba(139, 35, 50, 0.08)'
                      : 'transparent',
                    borderLeft: isActive
                      ? '3px solid #8B2332'
                      : '3px solid transparent',
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.background =
                        'rgba(139, 35, 50, 0.04)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* New Run Button */}
          <div className="px-6 mt-8">
            <Button
              variant="primary"
              fullWidth
              onClick={() => setShowWizard(true)}
            >
              New Eval Run
            </Button>
          </div>

          <div className="px-6 mt-8 text-sm" style={{ color: '#A0A0A0' }}>
            <p className="m-0">
              Internal testing and evaluation system for baseline model
              performance.
            </p>
          </div>
        </aside>

        {/* Execution Wizard */}
        {showWizard && (
          <ExecutionWizard
            onClose={() => setShowWizard(false)}
            onSuccess={runId => {
              setShowWizard(false);
              navigate('/eval/results');
            }}
          />
        )}

        {/* Main content */}
        <main className="ml-60 flex-1 p-12 max-w-6xl">{children}</main>

        {/* Selection Panel - floating bottom right */}
        <SelectionPanel />
      </div>
    </SelectionProvider>
  );
}
